import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { formatCOP, formatDate, getScoreBadge, getRupBadge, getCountdown, getAIPriorityBadge, requiresRup } from "@/utils/formatting";
import { TEST_IDS } from "@/constants/testIds";
import {
  ArrowLeft, Star, StarOff, ExternalLink, Clock, Building2,
  MapPin, Calendar, DollarSign, Hash, AlertCircle, Loader2,
  CheckCircle2, HelpCircle, Sparkles, RefreshCw, Brain, Tag, Shield,
  Target, ChevronRight,
} from "lucide-react";

export default function ProcessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [favoriting, setFavoriting] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);
  const [decision, setDecision] = useState(null);
  const [savingDecision, setSavingDecision] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ decision: "", reason: "", notes: "" });

  useEffect(() => {
    api
      .get(`/processes/${id}`)
      .then((r) => setProcess(r.data))
      .catch(() => setError("Proceso no encontrado"))
      .finally(() => setLoading(false));
    // Load existing decision
    api.get(`/decisions/${id}`)
      .then(r => { if (r.data.decision) { setDecision(r.data); setDecisionForm({ decision: r.data.decision, reason: r.data.reason || "", notes: r.data.notes || "" }); } })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!process?.closing_date) return;
    const update = () => setCountdown(getCountdown(process.closing_date));
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [process?.closing_date]);

  const toggleFavorite = async () => {
    if (!process) return;
    setFavoriting(true);
    try {
      if (process.is_favorite) {
        await api.delete(`/favorites/${process.id}`);
        setProcess((p) => ({ ...p, is_favorite: false }));
      } else {
        await api.post("/favorites", { opportunity_id: process.id });
        setProcess((p) => ({ ...p, is_favorite: true }));
      }
    } catch (_) {}
    setFavoriting(false);
  };

  const loadAiAnalysis = async () => {
    if (!process) return;
    setAiLoading(true);
    try {
      const r = await api.post("/processes/ai-analyze", { process_id: process.id });
      setAiAnalysis(r.data);
    } catch (e) {
      setAiAnalysis({ status: "ERROR", message: "Análisis no disponible" });
    } finally {
      setAiLoading(false);
    }
  };

  const triggerBatchAnalysis = async () => {
    if (!process) return;
    setBatchAnalyzing(true);
    try {
      const r = await api.post(`/analysis/process/${process.id}`, null, { params: { force: true } });
      // Refresh process data to get new AI fields
      const updated = await api.get(`/processes/${process.id}`);
      setProcess((prev) => ({ ...prev, ...updated.data }));
    } catch (e) {
      // Silent fail
    } finally {
      setBatchAnalyzing(false);
    }
  };

  const saveDecision = async () => {
    if (!decisionForm.decision) return;
    setSavingDecision(true);
    try {
      const r = await api.post(`/decisions/${id}`, decisionForm);
      setDecision(r.data);
      const { toast } = await import("sonner");
      toast.success("Decisión guardada");
    } catch (_) {
      const { toast } = await import("sonner");
      toast.error("Error guardando decisión");
    } finally {
      setSavingDecision(false);
    }
  };

  if (loading) return <DetailSkeleton />;
  if (error) return (
    <div className="p-6 text-center">
      <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
      <p className="text-slate-400">{error}</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-emerald-400 text-sm hover:text-emerald-300">
        Volver al Radar
      </button>
    </div>
  );

  const badge = getScoreBadge(process.score_category, process.score);
  // RUP: priorizar IA > regla basada en modalidad > no verificado
  const aiRupStatus = process.ai_rup_status || process.rup_status;
  const ruleRup = requiresRup(process.modality);
  let rupResult = "NO_VERIFICADO";
  if (aiRupStatus && aiRupStatus !== "RUP_NO_VERIFICADO") {
    rupResult = aiRupStatus;
  } else if (ruleRup === "NO_APLICA") {
    rupResult = "RUP_NO_APLICA";
  }
  const rupBadge = getRupBadge(rupResult);
  // Source label for RUP
  const rupSource = aiRupStatus && aiRupStatus !== "RUP_NO_VERIFICADO"
    ? "Fuente: Análisis IA"
    : ruleRup === "NO_APLICA"
      ? "Inferido desde modalidad — verificar en pliego"
      : "Pendiente de análisis";

  const rel = process.relevance_score;
  const compat = process.compatibility_score;
  const hasV2Scores = rel != null && compat != null;

  return (
    <div data-testid={TEST_IDS.PROCESS_DETAIL} className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5">
      {/* Back nav */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Volver al Radar
      </button>

      {/* Header card */}
      <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span data-testid={TEST_IDS.PROCESS_SCORE_BADGE} className={`text-xs px-2.5 py-1 rounded-full font-semibold ${badge.cls}`}>
                {badge.label}
              </span>
              <span className="font-mono text-slate-400 text-sm font-bold">{process.score}/100</span>
              {/* RUP with source */}
              <span title={rupSource} className={`text-xs px-2 py-0.5 rounded-full cursor-help ${rupBadge.cls}`}>
                {rupBadge.label}
              </span>
              <span className="text-slate-600 text-[9px]">({rupSource})</span>
            </div>

            {/* Dual score breakdown (v2 only) */}
            {hasV2Scores && (
              <div className="flex gap-3 mb-3 flex-wrap">
                <div className="bg-slate-800/70 border border-slate-700/40 rounded-lg px-3 py-2 flex-1 min-w-[120px]">
                  <p className="text-slate-500 text-[9px] uppercase tracking-wide mb-1">Relevancia</p>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400 font-mono font-bold text-sm">{rel}/100</span>
                    <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${rel}%` }} />
                    </div>
                  </div>
                  <p className="text-slate-600 text-[9px] mt-1">{process.relevance_factors?.[0] || "Coincidencia con tu nicho"}</p>
                </div>
                <div className="bg-slate-800/70 border border-slate-700/40 rounded-lg px-3 py-2 flex-1 min-w-[120px]">
                  <p className="text-slate-500 text-[9px] uppercase tracking-wide mb-1">Compatibilidad</p>
                  <div className="flex items-center gap-2">
                    <span className="text-blue-400 font-mono font-bold text-sm">{compat}/100</span>
                    <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${compat}%` }} />
                    </div>
                  </div>
                  <p className="text-slate-600 text-[9px] mt-1">{process.compatibility_factors?.[0] || "Ubicación, valor, modalidad"}</p>
                </div>
              </div>
            )}
            {hasV2Scores && (
              <p className="text-slate-600 text-[9px] italic mb-2">
                Estimación basada en datos estructurados SECOP. Pendiente de verificar en documentos oficiales del proceso.
              </p>
            )}

            <h1 className="font-outfit font-bold text-lg lg:text-xl text-slate-50 leading-snug">
              {process.object || "Proceso sin descripción"}
            </h1>
            <p className="text-slate-400 text-sm mt-1 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              {process.entity || "Entidad no especificada"}
              {process.entity_nit && <span className="text-slate-600 font-mono text-xs">· NIT {process.entity_nit}</span>}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid={TEST_IDS.PROCESS_FAVORITE_BTN}
              onClick={toggleFavorite}
              disabled={favoriting}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-all ${
                process.is_favorite
                  ? "bg-amber-500/15 border-amber-500/30 text-amber-400"
                  : "bg-slate-800 border-slate-700 text-slate-400 hover:text-amber-400 hover:border-amber-500/30"
              }`}
            >
              {process.is_favorite ? <Star className="w-3.5 h-3.5 fill-current" /> : <StarOff className="w-3.5 h-3.5" />}
              {process.is_favorite ? "Guardado" : "Guardar"}
            </button>
            {process.source_url && (
              <a
                data-testid={TEST_IDS.PROCESS_SOURCE_LINK}
                href={process.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-sm transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                SECOP II
              </a>
            )}
          </div>
        </div>

        {/* Countdown */}
        {countdown && !countdown.expired && (
          <div data-testid={TEST_IDS.PROCESS_COUNTDOWN} className="mt-4 flex items-center gap-3 bg-slate-800/50 rounded-xl px-4 py-3">
            <Clock className="w-4 h-4 text-amber-400" />
            <div>
              <p className="text-slate-400 text-xs">Tiempo hasta cierre</p>
              <p className="text-amber-400 font-mono font-bold text-sm">
                {countdown.days}d {countdown.hours}h {countdown.minutes}m
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-slate-500 text-xs">Fecha cierre</p>
              <p className="text-slate-200 text-xs font-medium">{formatDate(process.closing_date)}</p>
            </div>
          </div>
        )}
        {countdown?.expired && (
          <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2 text-red-400 text-sm">
            Proceso vencido
          </div>
        )}
      </div>

      {/* Two column layout */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Process data */}
          <DetailCard title="Información del Proceso">
            <DetailGrid>
              <DetailItem icon={<Hash />} label="N° Proceso" value={process.process_number || process.external_id} mono />
              <DetailItem icon={<MapPin />} label="Departamento" value={process.department} />
              <DetailItem icon={<MapPin />} label="Ciudad" value={process.city} />
              <DetailItem icon={<Calendar />} label="Modalidad" value={process.modality} />
              <DetailItem icon={<DollarSign />} label="Valor estimado" value={formatCOP(process.value)} mono />
              <DetailItem icon={<Calendar />} label="Publicado" value={formatDate(process.publication_date)} />
              <DetailItem icon={<Clock />} label="Cierre" value={formatDate(process.closing_date)} />
              <DetailItem icon={<Hash />} label="UNSPSC" value={process.unspsc_code} mono />
              <DetailItem icon={<Hash />} label="Estado" value={process.status} />
            </DetailGrid>
          </DetailCard>

          {/* Score breakdown */}
          {process.score_details && (
            <DetailCard title="Desglose del Score">
              <ScoreBreakdown details={process.score_details} total={process.score} />
            </DetailCard>
          )}

          {/* Source info */}
          <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl px-4 py-3 flex items-start gap-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-emerald-400 text-xs font-semibold">FUENTE OFICIAL VERIFICADA</p>
              <p className="text-slate-400 text-xs mt-0.5">
                Datos obtenidos directamente de SECOP II (datos.gov.co).
                Última actualización: {formatDate(process.last_updated)}.
              </p>
              {process.source_url && (
                <a href={process.source_url} target="_blank" rel="noopener noreferrer"
                  className="text-emerald-400 text-xs hover:text-emerald-300 mt-1 inline-flex items-center gap-1">
                  Ver proceso en SECOP II <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Right: AI analysis */}
        <div className="space-y-4">
          {/* Batch AI Classification Panel */}
          <div data-testid="process-ai-batch" className="bg-slate-900 border border-slate-700/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-4 h-4 text-emerald-400" />
              <h3 className="font-outfit font-semibold text-slate-200 text-sm">Clasificación IA</h3>
              <span className="text-[9px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded-full font-mono">Gemini Flash</span>
            </div>

            {process.ai_priority ? (
              <BatchAnalysisResult
                process={process}
                onReanalyze={triggerBatchAnalysis}
                analyzing={batchAnalyzing}
              />
            ) : (
              <div>
                <p className="text-slate-500 text-xs mb-3">
                  Clasifica este proceso automáticamente con Gemini 3 Flash para determinar su prioridad.
                </p>
                <button
                  data-testid="analyze-batch-btn"
                  onClick={triggerBatchAnalysis}
                  disabled={batchAnalyzing}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 text-sm py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {batchAnalyzing ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Clasificando...</>
                  ) : (
                    <><Brain className="w-3.5 h-3.5" /> Clasificar con IA</>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Deep AI analysis (Claude Sonnet) */}
          <div data-testid={TEST_IDS.PROCESS_AI_ANALYSIS} className="bg-slate-900 border border-slate-700/50 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <h3 className="font-outfit font-semibold text-slate-200 text-sm">Análisis Profundo</h3>
              <span className="text-[9px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">Claude Sonnet</span>
            </div>

            {!aiAnalysis && !aiLoading && (
              <div>
                <p className="text-slate-500 text-xs mb-3">
                  Análisis detallado: requisitos clave, riesgos, recomendación estratégica.
                </p>
                <button
                  onClick={loadAiAnalysis}
                  className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-300 text-sm py-2 rounded-lg transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Análisis Profundo con IA
                </button>
              </div>
            )}

            {aiLoading && (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-4 justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                Analizando proceso...
              </div>
            )}

            {aiAnalysis && !aiLoading && (
              <AiAnalysisResult analysis={aiAnalysis} onRefresh={loadAiAnalysis} />
            )}
          </div>

          {/* Truth state legend */}
          <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-4">
            <h3 className="font-outfit font-semibold text-slate-200 text-sm mb-3 flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-slate-500" />
              Estados de Verdad
            </h3>
            <div className="space-y-2 text-xs">
              {[
                { label: "🟢 CONFIRMADO", cls: "state-confirmed", desc: "Verificado con documentos" },
                { label: "🟡 INFERIDO", cls: "state-inferred", desc: "Estimado por análisis" },
                { label: "🔴 NO CUMPLE", cls: "state-no-comply", desc: "Descartado por requisitos" },
                { label: "⚪ NO VERIFICADO", cls: "state-unverified", desc: "Pendiente de análisis" },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${s.cls}`}>{s.label}</span>
                  <span className="text-slate-500">{s.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Mi Decisión */}
          <div data-testid="process-decision-panel" className="bg-slate-900 border border-slate-700/50 rounded-2xl p-4">
            <h3 className="font-outfit font-semibold text-slate-200 text-sm mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-slate-400" />
              Mi Decisión
              {decision?.decision && (
                <DecisionBadge decision={decision.decision} />
              )}
            </h3>
            <div className="space-y-3">
              <select
                value={decisionForm.decision}
                onChange={e => setDecisionForm(f => ({ ...f, decision: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:border-emerald-500/50"
              >
                <option value="">Seleccionar decisión...</option>
                {[
                  { v: "INTERESADO",    l: "Interesado" },
                  { v: "EN_SEGUIMIENTO", l: "En Seguimiento" },
                  { v: "PARTICIPANDO",  l: "Participando" },
                  { v: "DESCARTADO",    l: "Descartado" },
                  { v: "GANADO",        l: "Ganado" },
                  { v: "PERDIDO",       l: "Perdido" },
                ].map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <input
                type="text"
                value={decisionForm.reason}
                onChange={e => setDecisionForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Razón (opcional)"
                className="w-full bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:border-emerald-500/50 placeholder-slate-600"
              />
              <textarea
                value={decisionForm.notes}
                onChange={e => setDecisionForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notas adicionales..."
                rows={2}
                className="w-full bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-2.5 py-2 focus:outline-none focus:border-emerald-500/50 placeholder-slate-600 resize-none"
              />
              <button
                onClick={saveDecision}
                disabled={savingDecision || !decisionForm.decision}
                data-testid="save-decision-btn"
                className="w-full flex items-center justify-center gap-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 text-xs py-2 rounded-lg transition-colors disabled:opacity-40"
              >
                {savingDecision ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Guardar decisión
              </button>
            </div>
          </div>

          {/* Expediente vs. Proceso */}
          <Link
            to={`/expediente/${id}`}
            data-testid="go-to-dossier-btn"
            className="flex items-center gap-3 bg-slate-900 border border-slate-700/50 rounded-2xl p-4 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
          >
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 group-hover:bg-emerald-500/20 transition-colors">
              <Target className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-200 text-sm font-medium">Expediente vs. Proceso</p>
              <p className="text-slate-500 text-xs">Análisis IA de compatibilidad empresa-proceso</p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 transition-colors shrink-0" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function DetailCard({ title, children }) {
  return (
    <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-4">
      <h3 className="font-outfit font-semibold text-slate-200 text-sm mb-4">{title}</h3>
      {children}
    </div>
  );
}

function DetailGrid({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

function DetailItem({ icon, label, value, mono = false }) {
  return (
    <div>
      <p className="text-slate-500 text-[10px] uppercase tracking-wide mb-0.5 flex items-center gap-1">
        {React.cloneElement(icon, { className: "w-3 h-3" })}
        {label}
      </p>
      <p className={`text-slate-200 text-xs ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
}

function ScoreBreakdown({ details, total }) {
  const items = [
    { label: "Palabras clave", key: "keyword_score", max: 50, note: details.keyword_match },
    { label: "Ubicación", key: "location_score", max: 15 },
    { label: "Rango de valor", key: "value_score", max: 15 },
    { label: "UNSPSC", key: "unspsc_score", max: 10 },
  ];
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const val = details[item.key] || 0;
        const pct = Math.max(0, Math.min(100, (val / item.max) * 100));
        return (
          <div key={item.key}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{item.label}</span>
              <span className={`font-mono ${val > 0 ? "text-emerald-400" : val < 0 ? "text-red-400" : "text-slate-500"}`}>
                {val > 0 ? "+" : ""}{val}/{item.max}
              </span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${val > 0 ? "bg-emerald-500" : val < 0 ? "bg-red-500" : "bg-slate-600"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            {item.note && <p className="text-slate-600 text-[10px] mt-0.5 truncate">{item.note}</p>}
          </div>
        );
      })}
      {details.penalty < 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 text-xs">
          <span className="text-red-400">Penalización: {details.penalty} pts</span>
          {details.penalty_match && <span className="text-red-600 ml-1">({details.penalty_match})</span>}
        </div>
      )}
      <div className="border-t border-slate-700 pt-2 flex justify-between">
        <span className="text-slate-400 text-xs font-medium">Score total</span>
        <span className="font-mono font-bold text-slate-100">{total}/100</span>
      </div>
    </div>
  );
}

function AiAnalysisResult({ analysis, onRefresh }) {
  if (analysis.status === "ERROR" || analysis.status === "NOT_CONFIGURED") {
    return <p className="text-slate-500 text-xs">{analysis.message || "Análisis no disponible"}</p>;
  }

  // Support both old (PARTICIPAR/EVALUAR/DESCARTAR) and new (ALTA_COMPATIBILIDAD/REQUIERE_REVISION/BAJA_COMPATIBILIDAD) values
  const REC_CONFIG = {
    ALTA_COMPATIBILIDAD:  { cls: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20", label: "Alta Compatibilidad" },
    REQUIERE_REVISION:    { cls: "bg-amber-500/15 text-amber-300 border border-amber-500/20",       label: "Requiere Revisión" },
    BAJA_COMPATIBILIDAD:  { cls: "bg-red-500/15 text-red-300 border border-red-500/20",             label: "Baja Compatibilidad" },
    // Backward compat
    PARTICIPAR:           { cls: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20", label: "Alta Compatibilidad" },
    EVALUAR:              { cls: "bg-amber-500/15 text-amber-300 border border-amber-500/20",       label: "Requiere Revisión" },
    DESCARTAR:            { cls: "bg-red-500/15 text-red-300 border border-red-500/20",             label: "Baja Compatibilidad" },
  };
  const rec = REC_CONFIG[analysis.recomendacion];

  return (
    <div className="space-y-3 text-xs">
      {/* Disclaimer */}
      <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg px-3 py-2 text-amber-400/70 text-[10px]">
        Análisis basado en datos estructurados SECOP. No se han analizado pliegos ni documentos adjuntos del proceso.
      </div>

      {/* Recommendation */}
      {analysis.recomendacion && (
        <div className={`rounded-lg p-2.5 ${rec?.cls || "bg-slate-800/50"}`}>
          <span className="font-bold text-sm">{rec?.label || analysis.recomendacion}</span>
          {analysis.razon_recomendacion && (
            <p className="text-current opacity-80 mt-0.5">{analysis.razon_recomendacion}</p>
          )}
        </div>
      )}

      {/* Summary */}
      {analysis.resumen_ejecutivo && (
        <p className="text-slate-400 leading-relaxed">{analysis.resumen_ejecutivo}</p>
      )}

      {/* Matching factors */}
      {analysis.matching_factors?.length > 0 && (
        <div>
          <p className="text-slate-500 uppercase tracking-wide text-[10px] mb-1">Por qué aplica</p>
          <ul className="space-y-0.5">
            {analysis.matching_factors.map((r, i) => <li key={i} className="text-slate-400">✓ {r}</li>)}
          </ul>
        </div>
      )}

      {/* Requirements to verify */}
      {(analysis.requirements_to_verify?.length > 0 || analysis.requisitos_clave?.length > 0) && (
        <div className="bg-blue-500/5 border border-blue-500/15 rounded-lg p-2.5">
          <p className="text-blue-400/80 text-[10px] uppercase tracking-wide mb-1.5">Por verificar en pliego oficial</p>
          <ul className="space-y-0.5">
            {(analysis.requirements_to_verify || analysis.requisitos_clave || []).map((r, i) => (
              <li key={i} className="text-slate-400">⚠ {r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Risks */}
      {analysis.riesgos?.length > 0 && (
        <div>
          <p className="text-slate-500 uppercase tracking-wide text-[10px] mb-1">Riesgos identificados</p>
          <ul className="space-y-0.5">
            {analysis.riesgos.map((r, i) => <li key={i} className="text-slate-500">• {r}</li>)}
          </ul>
        </div>
      )}

      {/* Missing info */}
      {analysis.missing_information?.length > 0 && (
        <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-2.5">
          <p className="text-slate-500 text-[10px] uppercase tracking-wide mb-1">Información no disponible</p>
          <ul className="space-y-0.5">
            {analysis.missing_information.map((m, i) => (
              <li key={i} className="text-slate-600">— {m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Confidence */}
      {analysis.confidence && (
        <p className="text-slate-600 text-[10px]">
          Confianza del análisis: <span className="text-slate-500 font-medium">{analysis.confidence}</span>
          {analysis.confidence_basis && ` — ${analysis.confidence_basis}`}
        </p>
      )}

      <button onClick={onRefresh} className="flex items-center gap-1 text-slate-600 hover:text-slate-400 text-[10px] transition-colors mt-2">
        <RefreshCw className="w-3 h-3" /> Actualizar análisis
      </button>
    </div>
  );
}

function BatchAnalysisResult({ process, onReanalyze, analyzing }) {
  const aiBadge = getAIPriorityBadge(process.ai_priority);
  const rupBadge = getRupBadge(process.ai_rup_status || "RUP_NO_VERIFICADO");

  const opportunityColors = {
    EXCELENTE: "text-emerald-400",
    BUENA: "text-green-400",
    MEDIA: "text-amber-400",
    BAJA: "text-orange-400",
    DESCARTADA: "text-slate-500",
  };

  return (
    <div className="space-y-3 text-xs">
      {/* Disclaimer */}
      <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg px-3 py-2 text-amber-400/70 text-[10px]">
        Análisis batch basado en datos SECOP. No se han analizado pliegos ni documentos del proceso.
      </div>

      {/* Priority badge + score */}
      <div className="flex items-center gap-2 flex-wrap">
        {aiBadge && (
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${aiBadge.cls}`}>
            {aiBadge.label}
          </span>
        )}
        {process.ai_score != null && (
          <span className="font-mono text-slate-300 text-sm font-bold">{process.ai_score}/100</span>
        )}
        {process.ai_opportunity_level && (
          <span className={`font-medium ${opportunityColors[process.ai_opportunity_level] || "text-slate-400"}`}>
            {process.ai_opportunity_level}
          </span>
        )}
      </div>

      {/* Summary */}
      {process.ai_summary && (
        <p className="text-slate-400 leading-relaxed">{process.ai_summary}</p>
      )}

      {/* RUP Status */}
      <div>
        <div className="flex items-center gap-2">
          <Shield className="w-3 h-3 text-slate-500 shrink-0" />
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${rupBadge.cls}`}>{rupBadge.label}</span>
        </div>
        {process.ai_rup_basis && (
          <p className="text-slate-600 text-[9px] mt-0.5 ml-5">{process.ai_rup_basis}</p>
        )}
      </div>

      {/* Requirements to verify */}
      {process.ai_requirements_to_verify?.length > 0 && (
        <div className="bg-blue-500/5 border border-blue-500/15 rounded-lg p-2.5">
          <p className="text-blue-400/80 text-[10px] uppercase tracking-wide mb-1">Por verificar en pliego oficial</p>
          <ul className="space-y-0.5">
            {process.ai_requirements_to_verify.map((r, i) => (
              <li key={i} className="text-slate-400">⚠ {r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Keywords found */}
      {process.ai_keywords_found?.length > 0 && (
        <div>
          <p className="text-slate-500 uppercase tracking-wide text-[10px] mb-1 flex items-center gap-1">
            <Tag className="w-2.5 h-2.5" /> Palabras clave detectadas
          </p>
          <div className="flex flex-wrap gap-1">
            {process.ai_keywords_found.map((kw, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                {kw}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Risk flags */}
      {process.ai_risk_flags?.length > 0 && (
        <div>
          <p className="text-slate-500 uppercase tracking-wide text-[10px] mb-1">Factores de riesgo</p>
          <div className="flex flex-wrap gap-1">
            {process.ai_risk_flags.map((flag, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                {flag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Justification (collapsible) */}
      {process.ai_justification && (
        <details className="group">
          <summary className="text-slate-600 hover:text-slate-400 cursor-pointer text-[10px] transition-colors">
            Ver justificación completa
          </summary>
          <p className="text-slate-500 leading-relaxed mt-1 text-[10px]">{process.ai_justification}</p>
        </details>
      )}

      {/* Re-analyze button */}
      <button
        onClick={onReanalyze}
        disabled={analyzing}
        className="flex items-center gap-1 text-slate-600 hover:text-slate-400 text-[10px] transition-colors"
      >
        {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        {analyzing ? "Re-analizando..." : "Re-analizar"}
      </button>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto animate-pulse">
      <div className="h-6 bg-slate-800 rounded w-24" />
      <div className="h-40 bg-slate-800 rounded-2xl" />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 h-64 bg-slate-800 rounded-2xl" />
        <div className="h-64 bg-slate-800 rounded-2xl" />
      </div>
    </div>
  );
}

function DecisionBadge({ decision }) {
  const config = {
    INTERESADO:    "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
    EN_SEGUIMIENTO: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    PARTICIPANDO:  "bg-purple-500/15 text-purple-400 border-purple-500/20",
    DESCARTADO:    "bg-slate-700 text-slate-400 border-slate-600",
    GANADO:        "bg-amber-500/15 text-amber-400 border-amber-500/20",
    PERDIDO:       "bg-red-500/15 text-red-400 border-red-500/20",
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ml-1 ${config[decision] || config.DESCARTADO}`}>
      {decision}
    </span>
  );
}
